import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useTeam } from './TeamContext';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { TEAM_CONFIG_PRICING } from '../data/defaultTeamConfigs';

const TeamConfigContext = createContext();

export const useTeamConfig = () => {
  const context = useContext(TeamConfigContext);
  if (!context) {
    throw new Error('useTeamConfig must be used within TeamConfigProvider');
  }
  return context;
};

export const TeamConfigProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [wards, setWards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch team configs
  useEffect(() => {
    if (!currentTeam) {
      setLocations([]);
      setDepartments([]);
      setWards([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Fetch Locations
    const locationsUnsubscribe = onSnapshot(
      query(
        collection(db, 'teamConfigs'),
        where('teamId', '==', currentTeam.id),
        where('type', '==', 'LOCATIONS')
      ),
      (snapshot) => {
        if (!snapshot.empty) {
          const configData = snapshot.docs[0].data();
          setLocations(configData.values || []);
        } else {
          setLocations([]);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching locations:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    // Fetch Departments
    const departmentsUnsubscribe = onSnapshot(
      query(
        collection(db, 'teamConfigs'),
        where('teamId', '==', currentTeam.id),
        where('type', '==', 'DEPARTMENTS')
      ),
      (snapshot) => {
        if (!snapshot.empty) {
          const configData = snapshot.docs[0].data();
          setDepartments(configData.values || []);
        } else {
          setDepartments([]);
        }
      },
      (err) => {
        console.error('Error fetching departments:', err);
      }
    );

    // Fetch Wards
    const wardsUnsubscribe = onSnapshot(
      query(
        collection(db, 'teamConfigs'),
        where('teamId', '==', currentTeam.id),
        where('type', '==', 'WARDS')
      ),
      (snapshot) => {
        if (!snapshot.empty) {
          const configData = snapshot.docs[0].data();
          setWards(configData.values || []);
        } else {
          setWards([]);
        }
      },
      (err) => {
        console.error('Error fetching wards:', err);
      }
    );

    return () => {
      locationsUnsubscribe();
      departmentsUnsubscribe();
      wardsUnsubscribe();
    };
  }, [currentTeam]);

  // Add Location
  const addLocation = async (locationName) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์เพิ่มตำแหน่งเก็บอุปกรณ์');
      }

      // Get current config
      const configQuery = query(
        collection(db, 'teamConfigs'),
        where('teamId', '==', currentTeam.id),
        where('type', '==', 'LOCATIONS')
      );
      const configSnapshot = await getDocs(configQuery);

      if (configSnapshot.empty) {
        throw new Error('ไม่พบ config สำหรับตำแหน่งเก็บอุปกรณ์');
      }

      const configDoc = configSnapshot.docs[0];
      const configData = configDoc.data();

      // Check if location already exists
      if (configData.values.includes(locationName)) {
        throw new Error('ตำแหน่งเก็บอุปกรณ์นี้มีอยู่แล้ว');
      }

      // Update config
      await updateDoc(doc(db, 'teamConfigs', configDoc.id), {
        values: [...configData.values, locationName],
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error adding location:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Add Department
  const addDepartment = async (departmentName) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์เพิ่มแผนก');
      }

      // Get current config
      const configQuery = query(
        collection(db, 'teamConfigs'),
        where('teamId', '==', currentTeam.id),
        where('type', '==', 'DEPARTMENTS')
      );
      const configSnapshot = await getDocs(configQuery);

      if (configSnapshot.empty) {
        throw new Error('ไม่พบ config สำหรับแผนก');
      }

      const configDoc = configSnapshot.docs[0];
      const configData = configDoc.data();

      // Check if department already exists
      if (configData.values.includes(departmentName)) {
        throw new Error('แผนกนี้มีอยู่แล้ว');
      }

      // Update config
      await updateDoc(doc(db, 'teamConfigs', configDoc.id), {
        values: [...configData.values, departmentName],
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error adding department:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Add Ward
  const addWard = async (wardName) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์เพิ่มหอผู้ป่วย');
      }

      // Get current config
      const configQuery = query(
        collection(db, 'teamConfigs'),
        where('teamId', '==', currentTeam.id),
        where('type', '==', 'WARDS')
      );
      const configSnapshot = await getDocs(configQuery);

      if (configSnapshot.empty) {
        throw new Error('ไม่พบ config สำหรับหอผู้ป่วย');
      }

      const configDoc = configSnapshot.docs[0];
      const configData = configDoc.data();

      // Check if ward already exists
      if (configData.values.includes(wardName)) {
        throw new Error('หอผู้ป่วยนี้มีอยู่แล้ว');
      }

      // Update config
      await updateDoc(doc(db, 'teamConfigs', configDoc.id), {
        values: [...configData.values, wardName],
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error adding ward:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Delete Location
  const deleteLocation = async (locationName) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์ลบตำแหน่ง');
      }

      // Get current config
      const configQuery = query(
        collection(db, 'teamConfigs'),
        where('teamId', '==', currentTeam.id),
        where('type', '==', 'LOCATIONS')
      );
      const configSnapshot = await getDocs(configQuery);

      if (configSnapshot.empty) {
        throw new Error('ไม่พบ config สำหรับตำแหน่ง');
      }

      const configDoc = configSnapshot.docs[0];
      const configData = configDoc.data();

      // Check if it's a default value
      if (configData.isDefault) {
        throw new Error('ไม่สามารถลบตำแหน่ง default ได้');
      }

      // Update config
      await updateDoc(doc(db, 'teamConfigs', configDoc.id), {
        values: configData.values.filter(v => v !== locationName),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error deleting location:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Delete Department
  const deleteDepartment = async (departmentName) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์ลบแผนก');
      }

      // Get current config
      const configQuery = query(
        collection(db, 'teamConfigs'),
        where('teamId', '==', currentTeam.id),
        where('type', '==', 'DEPARTMENTS')
      );
      const configSnapshot = await getDocs(configQuery);

      if (configSnapshot.empty) {
        throw new Error('ไม่พบ config สำหรับแผนก');
      }

      const configDoc = configSnapshot.docs[0];
      const configData = configDoc.data();

      // Check if it's a default value
      if (configData.isDefault) {
        throw new Error('ไม่สามารถลบแผนก default ได้');
      }

      // Update config
      await updateDoc(doc(db, 'teamConfigs', configDoc.id), {
        values: configData.values.filter(v => v !== departmentName),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error deleting department:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Delete Ward
  const deleteWard = async (wardName) => {
    try {
      if (!currentUser || !currentTeam) {
        throw new Error('กรุณาเข้าสู่ระบบและเลือกทีม');
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        throw new Error('คุณไม่มีสิทธิ์ลบหอผู้ป่วย');
      }

      // Get current config
      const configQuery = query(
        collection(db, 'teamConfigs'),
        where('teamId', '==', currentTeam.id),
        where('type', '==', 'WARDS')
      );
      const configSnapshot = await getDocs(configQuery);

      if (configSnapshot.empty) {
        throw new Error('ไม่พบ config สำหรับหอผู้ป่วย');
      }

      const configDoc = configSnapshot.docs[0];
      const configData = configDoc.data();

      // Check if it's a default value
      if (configData.isDefault) {
        throw new Error('ไม่สามารถลบหอผู้ป่วย default ได้');
      }

      // Update config
      await updateDoc(doc(db, 'teamConfigs', configDoc.id), {
        values: configData.values.filter(v => v !== wardName),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (err) {
      console.error('Error deleting ward:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const value = {
    locations,
    departments,
    wards,
    loading,
    error,
    addLocation,
    addDepartment,
    addWard,
    deleteLocation,
    deleteDepartment,
    deleteWard
  };

  return (
    <TeamConfigContext.Provider value={value}>
      {children}
    </TeamConfigContext.Provider>
  );
};

